/**
 * Meeting Performance typed API (Phase 7). Every shape — input body, path
 * params, resolved value — derives from the SERVER's route defs via the
 * type-only import below (erased at build; no server code reaches the bundle).
 * A server-side shape change breaks this file and its callers at compile time.
 *
 * `apiFetch` returns parsed JSON, so `Date` fields on handler return types
 * arrive as ISO strings on the wire. Every entry's resolved value is therefore
 * re-typed through `Serialized<...>` (Date → string, recursively) by the single
 * cast on the export — call behavior is unchanged; only the types reflect the
 * wire. (fitstreak handled its one Date-typed field manually; meeting-perf has
 * Date columns on nearly every row, so the mapping is systematic here.)
 *
 * Hand-rolled entries (same call shapes, def-derived outputs):
 * - createMeeting: its input schema applies defaults (`source`, per-speaker
 *   `email`/`isPrimary`, segment `startTime`/`endTime`), so the def's inferred
 *   input (the schema's OUTPUT type) over-requires fields the wire body may
 *   omit. The wire body — the schema's INPUT type — is restated here.
 * - analyzeMeeting + createGoal: their bodies are validated in-handler with
 *   inline parseJson (NOT `input:`) so the meeting/speaker ownership check can
 *   404 BEFORE body validation — the defs carry no input schema, so the typed
 *   caller derives no input type for them.
 */
import { apiFetch, typedRoute } from "@d12labs/client";
import type { meetingPerfRouteDefs } from "../server/routes";

/** A def's resolved value — the handler's return type minus raw Response branches. */
type OutputOf<D extends { handler: (ctx: never) => unknown }> = Exclude<
	Awaited<ReturnType<D["handler"]>>,
	Response
>;

/** JSON-serialized view of a handler type: Date → ISO string, recursively. */
type Serialized<T> = T extends Date
	? string
	: T extends (infer U)[]
		? Serialized<U>[]
		: T extends object
			? { [K in keyof T]: Serialized<T[K]> }
			: T;

type SerializedFn<F> = F extends (...args: infer A) => Promise<infer R>
	? (...args: A) => Promise<Serialized<R>>
	: F;
type SerializedApi<T> = { [K in keyof T]: SerializedFn<T[K]> };

/** Wire body of POST /meetings — createMeetingSchema's INPUT type, restated. */
type CreateMeetingBody = {
	title: string;
	source?: "manual" | "fireflies" | "zoom";
	meetingDate?: string | Date;
	durationMinutes?: number;
	participantCount?: number;
	speakers: {
		name: string;
		email?: string | null;
		isPrimary?: boolean;
		segments: { text: string; startTime?: string | null; endTime?: string | null }[];
	}[];
};

/** Mirrors the server's six scoring dimensions (scoring.ts CATEGORY_KEYS). */
type GoalCategory =
	| "clarity"
	| "conciseness"
	| "confidence"
	| "engagement"
	| "structure"
	| "professionalism";

const api = {
	listMeetings: typedRoute<typeof meetingPerfRouteDefs.listMeetings>()(
		"GET",
		"/api/plugins/meeting-perf/meetings",
	),

	// Hand-rolled (input schema applies defaults — see module docstring) — def-derived output.
	createMeeting: (opts: { input: CreateMeetingBody }) =>
		apiFetch<Serialized<OutputOf<typeof meetingPerfRouteDefs.createMeeting>>>(
			"/api/plugins/meeting-perf/meetings",
			{ method: "POST", body: JSON.stringify(opts.input) },
		),

	meetingDetail: typedRoute<typeof meetingPerfRouteDefs.meetingDetail>()(
		"GET",
		"/api/plugins/meeting-perf/meetings/:id",
	),
	removeMeeting: typedRoute<typeof meetingPerfRouteDefs.removeMeeting>()(
		"DELETE",
		"/api/plugins/meeting-perf/meetings/:id",
	),
	bulkRemoveMeetings: typedRoute<typeof meetingPerfRouteDefs.bulkRemoveMeetings>()(
		"POST",
		"/api/plugins/meeting-perf/meetings/bulk-delete",
	),

	// Hand-rolled (body validated in-handler so ownership 404s before the 400 —
	// no `input:` on the def) — def-derived output.
	analyzeMeeting: (opts: { params: { id: string }; input: { speakerId: string } }) =>
		apiFetch<Serialized<OutputOf<typeof meetingPerfRouteDefs.analyzeMeeting>>>(
			`/api/plugins/meeting-perf/meetings/${encodeURIComponent(opts.params.id)}/analyze`,
			{ method: "POST", body: JSON.stringify(opts.input) },
		),

	refetchTranscript: typedRoute<typeof meetingPerfRouteDefs.refetchTranscript>()(
		"POST",
		"/api/plugins/meeting-perf/meetings/:id/refetch-transcript",
	),
	fetchMedia: typedRoute<typeof meetingPerfRouteDefs.fetchMedia>()(
		"POST",
		"/api/plugins/meeting-perf/meetings/:id/fetch-media",
	),
	removeRecording: typedRoute<typeof meetingPerfRouteDefs.removeRecording>()(
		"DELETE",
		"/api/plugins/meeting-perf/recordings/:id",
	),

	listSpeakers: typedRoute<typeof meetingPerfRouteDefs.listSpeakers>()(
		"GET",
		"/api/plugins/meeting-perf/speakers",
	),
	speakerPerformance: typedRoute<typeof meetingPerfRouteDefs.speakerPerformance>()(
		"GET",
		"/api/plugins/meeting-perf/speakers/:id/performance",
	),
	speakerTrends: typedRoute<typeof meetingPerfRouteDefs.speakerTrends>()(
		"GET",
		"/api/plugins/meeting-perf/speakers/:id/trends",
	),
	listGoals: typedRoute<typeof meetingPerfRouteDefs.listGoals>()(
		"GET",
		"/api/plugins/meeting-perf/speakers/:id/goals",
	),

	// Hand-rolled (body validated in-handler so ownership 404s before the 400 —
	// no `input:` on the def) — def-derived output.
	createGoal: (opts: {
		params: { id: string };
		input: { category: GoalCategory; targetScore: number };
	}) =>
		apiFetch<Serialized<OutputOf<typeof meetingPerfRouteDefs.createGoal>>>(
			`/api/plugins/meeting-perf/speakers/${encodeURIComponent(opts.params.id)}/goals`,
			{ method: "POST", body: JSON.stringify(opts.input) },
		),

	dashboardSummary: typedRoute<typeof meetingPerfRouteDefs.dashboardSummary>()(
		"GET",
		"/api/plugins/meeting-perf/dashboard/summary",
	),

	setPrimary: typedRoute<typeof meetingPerfRouteDefs.setPrimary>()(
		"POST",
		"/api/plugins/meeting-perf/speakers/:id/primary",
	),
	setTracked: typedRoute<typeof meetingPerfRouteDefs.setTracked>()(
		"POST",
		"/api/plugins/meeting-perf/speakers/:id/track",
	),
	updateMeeting: typedRoute<typeof meetingPerfRouteDefs.updateMeeting>()(
		"PATCH",
		"/api/plugins/meeting-perf/meetings/:id",
	),
	setAffiliation: typedRoute<typeof meetingPerfRouteDefs.setAffiliation>()(
		"POST",
		"/api/plugins/meeting-perf/speakers/:id/affiliation",
	),

	firefliesStatus: typedRoute<typeof meetingPerfRouteDefs.firefliesStatus>()(
		"GET",
		"/api/plugins/meeting-perf/import/fireflies",
	),
	firefliesImport: typedRoute<typeof meetingPerfRouteDefs.firefliesImport>()(
		"POST",
		"/api/plugins/meeting-perf/import/fireflies",
	),

	activity: typedRoute<typeof meetingPerfRouteDefs.activity>()(
		"GET",
		"/api/plugins/meeting-perf/activity",
	),

	firefliesRemoved: typedRoute<typeof meetingPerfRouteDefs.firefliesRemoved>()(
		"GET",
		"/api/plugins/meeting-perf/import/fireflies/removed",
	),
	firefliesRestore: typedRoute<typeof meetingPerfRouteDefs.firefliesRestore>()(
		"DELETE",
		"/api/plugins/meeting-perf/import/fireflies/removed/:id",
	),

	firefliesBrowse: typedRoute<typeof meetingPerfRouteDefs.firefliesBrowse>()(
		"GET",
		"/api/plugins/meeting-perf/import/fireflies/browse",
	),

	mergeSpeakers: typedRoute<typeof meetingPerfRouteDefs.mergeSpeakers>()(
		"POST",
		"/api/plugins/meeting-perf/admin/speakers/merge",
	),

	coachSession: typedRoute<typeof meetingPerfRouteDefs.coachSession>()(
		"POST",
		"/api/plugins/meeting-perf/coach/session",
	),
	coachLatest: typedRoute<typeof meetingPerfRouteDefs.coachLatest>()(
		"GET",
		"/api/plugins/meeting-perf/coach/session/latest",
	),
	focusActive: typedRoute<typeof meetingPerfRouteDefs.focusActive>()(
		"GET",
		"/api/plugins/meeting-perf/focus/active",
	),
	focusEngaged: typedRoute<typeof meetingPerfRouteDefs.focusEngaged>()(
		"POST",
		"/api/plugins/meeting-perf/focus/:id/engaged",
	),
};

// The one cast (type-level only): re-map every entry's resolved value to its
// JSON-serialized form. `unknown` is required because Date → string is not
// assignable in either direction; the runtime functions are untouched.
export const meetingPerfApi = api as unknown as SerializedApi<typeof api>;
